import React, { useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material/";
import config from "./config";
import { strings } from "./common/i18n";
import tableState from "./common/lib/tableState";

const styles = {
    container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        gap: 2
    }
};

/**
 * Azure AD Logout Component
 * Clears authentication and navigates to login screen
 */
const AzureADLogout = () => {
    const { instance } = useMsal();
    const navigate = useNavigate();

    useEffect(() => {
        const handleLogout = async () => {
            console.log("🔓 Logging out...");

            // Clear app data
            sessionStorage.clear();
            localStorage.removeItem("persist:root");
            // Saved table views carry this user's own column filters and
            // global search text, and the org bucket is shared by everyone who
            // signs into that org on this device — so it must not outlive the
            // sign-out onto the next user.
            tableState.clearAllTableState();
            localStorage.removeItem("backend_auth_failed");
            localStorage.removeItem("backend_auth_error");
            localStorage.removeItem("session_expired");

            // Set logged out flag
            localStorage.setItem("user_logged_out", "true");

            // Check if there are any MSAL accounts
            const accounts = instance.getAllAccounts();

            if (accounts.length > 0) {
                // Perform full B2C logout to clear both client and server-side sessions
                // This will redirect to B2C logout, then back to our login page
                try {
                    await instance.logoutRedirect({
                        postLogoutRedirectUri: `${window.location.origin}/${config.paths.login}`
                    });
                    // Don't navigate here - logoutRedirect will handle the redirect
                    return;
                } catch (error) {
                    console.error("B2C logout redirect failed:", error);
                    // Fallback: clear cache and navigate
                    try {
                        await instance.clearCache();
                        console.log("✅ MSAL cache cleared (fallback)");
                    } catch (clearError) {
                        console.error(
                            "Failed to clear MSAL cache:",
                            clearError
                        );
                    }
                }
            } else {
                // No accounts, just clear cache
                try {
                    await instance.clearCache();
                    console.log("✅ MSAL cache cleared");
                } catch (error) {
                    console.error("Failed to clear MSAL cache:", error);
                }
            }

            // Navigate to login page (user will see "Logged Out" message)
            navigate(`/${config.paths.login}`, { replace: true });
        };

        handleLogout();
    }, [instance, navigate]);

    return (
        <Box sx={styles.container}>
            <CircularProgress />
            <Typography variant="body1">
                {strings("signingOut") || "Signing out..."}
            </Typography>
        </Box>
    );
};

export default AzureADLogout;
